import { isDestroyed, isDestroying } from '@ember/destroyable';
import { concat } from '@ember/helper';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismComboSelectInput } from '@mylighthouse/prism-ember';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { timeout } from 'lumen/utils/waiters';

interface HotelAndMarketFormFieldsSingleUserSearchSignature {
  Args: {
    selectItem: unknown;
    selectedOption: unknown;
  };
}

export default class HotelAndMarketFormFieldsSingleUserSearch extends Component<HotelAndMarketFormFieldsSingleUserSearchSignature> {
  @service declare ajax: Services['ajax'];
  @service declare errorTracking: Services['error-tracking'];
  @service declare intl: Services['intl'];

  @tracked isOpen = false;
  @tracked options = [];
  @tracked optionsFilter = '';
  @tracked page = 1;
  @tracked totalPages = 1;

  fetchOptionsTask = task({ restartable: true }, async () => {
    const data = {
      page: this.page - 1,
    };

    if (this.optionsFilter !== '') {
      // @ts-expect-error: Incorrect type
      data.q = this.optionsFilter;
    }

    // @ts-expect-error: Incorrect type
    let options = [];

    try {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const result = await this.ajax.request('/reports/autocomplete/users/', {
        method: 'GET',
        data,
      });

      // If we are not searching for a specific user, we can add the default option (all users)
      if (!this.optionsFilter) {
        const { defaultOption } = this;
        options.push(defaultOption);
      }
      // Add the results of the query
      options = [
        ...options,
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        ...result.results.map((user) => ({ label: user.email, id: user.id })),
      ];

      this.totalPages = Math.ceil(
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        result.meta.results_count / result.meta.results_per_page,
      );
    } catch (error) {
      this.errorTracking.addBreadcrumb({ data: { error } });
    }

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return options;
  });

  loadMoreTask = task({ restartable: true }, async () => {
    if (this.page + 1 < this.totalPages) {
      this.page += 1;
      const fetchedOptions = await this.fetchOptionsTask.perform();
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-assignment
      this.options = [...this.options, ...fetchedOptions];
    }
  });

  searchOptionTask = task({ restartable: true }, async (optionsFilter) => {
    await timeout();

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    this.optionsFilter = optionsFilter;
    this.page = 1;
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    this.options = await this.fetchOptionsTask.perform();
  });

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get defaultOption() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const label =
      // @ts-expect-error: Incorrect type
      this.args.defaultOptionLabel ?? this.intl.t('settings.all_users_filter');

    return {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      label,
    };
  }

  resetValues(): void {
    this.optionsFilter = '';
    this.page = 1;
    // @ts-expect-error: Incorrect type
    this.options = [this.defaultOption];
  }

  @action
  // @ts-expect-error: Incorrect type
  selectOption(option): void {
    this.optionsFilter = '';
    // @ts-expect-error: Incorrect type
    this.args.selectItem(option);
  }

  @action
  toggleDropdown(): void {
    if (isDestroyed(this) || isDestroying(this)) {
      return;
    }

    this.isOpen = !this.isOpen;

    if (this.isOpen) {
      void this.searchOptionTask.perform(this.optionsFilter);
    } else {
      this.resetValues();
    }
  }

  <template>
    <PrismComboSelectInput
      @horizontalPosition="right"
      @isSearchable={{true}}
      @loadingMessage={{concat (t "general.loading") "..."}}
      @onChange={{this.selectOption}}
      @onClose={{this.toggleDropdown}}
      @onOpen={{this.toggleDropdown}}
      @onSearch={{this.searchOptionTask.perform}}
      @options={{this.options}}
      @unsafe_onLoadMore={{this.loadMoreTask.perform}}
      @value={{if @selectedOption @selectedOption this.defaultOption}}
    />
  </template>
}
